// src/pages/Assessments.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { assessmentsApi, clientsApi, assessmentTypesApi } from '../services/api';
import DuplicateAssessmentDialog from '../components/DuplicateAssessmentDialog';

const Assessments = () => {
  const [assessments, setAssessments] = useState([]);
  const [clients, setClients] = useState([]);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [formData, setFormData] = useState({
    client_id: '',
    assessment_type_id: '',
    name: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [assessmentToDuplicate, setAssessmentToDuplicate] = useState(null);

  useEffect(() => {
    loadData();
  }, [filterStatus, filterClient]);

  const loadData = async () => {
    try {
      // Load assessments
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterClient !== 'all') params.client_id = filterClient;
      
      const assessmentsResponse = await assessmentsApi.getAll(params);
      setAssessments(assessmentsResponse.data.assessments || []);

      // Load clients for dropdown
      const clientsResponse = await clientsApi.getAll({ active: true });
      setClients(clientsResponse.data.clients || []);

      // Load assessment types
      const typesResponse = await assessmentTypesApi.getAll();
      setAssessmentTypes(typesResponse.data.assessment_types || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await assessmentsApi.create(formData);
      setShowModal(false);
      setFormData({
        client_id: '',
        assessment_type_id: '',
        name: '',
        description: ''
      });
      loadData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDuplicateDialog = (assessment) => {
    setAssessmentToDuplicate(assessment);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicate = async (options) => {
    if (!assessmentToDuplicate) return;
    
    try {
      const response = await assessmentsApi.duplicate(assessmentToDuplicate.id, options);
      alert(`Assessment duplicated successfully! New assessment: "${response.data.assessment.name}"`);
      loadData(); // Reload the list
      setDuplicateDialogOpen(false);
      setAssessmentToDuplicate(null);
    } catch (error) {
      console.error('Error duplicating assessment:', error);
      alert('Failed to duplicate assessment: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (assessment) => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete "${assessment.name}" and ALL related data (responses, scores, history). This action CANNOT be undone!\n\nAre you absolutely sure?`)) {
      return;
    }
    
    try {
      await assessmentsApi.delete(assessment.id);
      alert(`Assessment "${assessment.name}" has been permanently deleted.`);
      loadData(); // Reload the list
    } catch (error) {
      console.error('Error deleting assessment:', error);
      if (error.response?.status === 403) {
        alert('Access denied: Only administrators can delete assessments.');
      } else {
        alert('Failed to delete assessment: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const handleMigrateToCSF20 = async (assessment) => {
    if (!window.confirm(
      `🔄 Migrate "${assessment.name}" to NIST CSF 2.0?\n\n` +
      `This will:\n` +
      `• Create a new NIST CSF 2.0 assessment in DRAFT status\n` +
      `• Map responses from 1.1 controls to 2.0 controls\n` +
      `• Keep the original 1.1 assessment unchanged\n\n` +
      `Continue?`
    )) {
      return;
    }
    
    try {
      const response = await assessmentsApi.migrateToCSF20(assessment.id);
      const data = response.data;
      
      alert(
        `✅ Migration Successful!\n\n` +
        `New Assessment: "${data.new_assessment.name}"\n` +
        `Status: DRAFT\n` +
        `Responses Migrated: ${data.responses_migrated} of ${data.total_v2_questions} questions\n\n` +
        `The new assessment is ready for review and completion.`
      );
      
      loadData();
    } catch (error) {
      console.error('Error migrating assessment:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert(`❌ Migration Failed\n\n${errorMsg}`);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { bg: '#e9ecef', color: '#495057', label: 'DRAFT' },
      in_progress: { bg: '#fff3cd', color: '#856404', label: 'IN PROGRESS' },
      completed: { bg: '#d4edda', color: '#155724', label: 'COMPLETED' },
      reopened: { bg: '#d1ecf1', color: '#0c5460', label: 'REOPENED' }
    };
    const style = styles[status] || styles.draft;
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        backgroundColor: style.bg,
        color: style.color
      }}>
        {style.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1 style={{ margin: 0 }}>Assessments</h1>
        <button
          onClick={() => setShowModal(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#27ae60',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          + Create Assessment
        </button>
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        marginBottom: '20px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
            Status
          </label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              minWidth: '150px'
            }}
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="reopened">Reopened</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
            Client
          </label>
          <select
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
            style={{
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              minWidth: '200px'
            }}
          >
            <option value="all">All Clients</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>{client.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Assessments Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={tableHeaderStyle}>Assessment Name</th>
              <th style={tableHeaderStyle}>Client</th>
              <th style={tableHeaderStyle}>Type</th>
              <th style={tableHeaderStyle}>Status</th>
              <th style={tableHeaderStyle}>Progress</th>
              <th style={tableHeaderStyle}>Created</th>
              <th style={tableHeaderStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assessments.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                  No assessments found
                </td>
              </tr>
            ) : (
              assessments.map(assessment => (
                <tr key={assessment.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={tableCellStyle}>
                    <Link 
                      to={`/assessments/${assessment.id}`} 
                      style={{ color: '#3498db', textDecoration: 'none', fontWeight: 'bold' }}
                    >
                      {assessment.name}
                    </Link>
                    {assessment.description && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {assessment.description}
                      </div>
                    )}
                  </td>
                  <td style={tableCellStyle}>
                    <Link to={`/clients/${assessment.client_id}`} style={{ color: '#3498db', textDecoration: 'none' }}>
                      {assessment.client_name}
                    </Link>
                  </td>
                  <td style={tableCellStyle}>{assessment.assessment_type_name}</td>
                  <td style={tableCellStyle}>{getStatusBadge(assessment.status)}</td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        flex: 1,
                        height: '8px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${assessment.progress_percentage || 0}%`,
                          height: '100%',
                          backgroundColor: '#27ae60',
                          transition: 'width 0.3s'
                        }} />
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 'bold', minWidth: '35px' }}>
                        {assessment.progress_percentage || 0}%
                      </span>
                    </div>
                  </td>
                  <td style={tableCellStyle}>{formatDate(assessment.created_at)}</td>
                  <td style={tableCellStyle}>
                    {assessment.status === 'draft' || assessment.status === 'in_progress' || assessment.status === 'reopened' ? (
                      <Link
                        to={`/assessments/${assessment.id}/take`}
                        style={{
                          padding: '5px 10px',
                          backgroundColor: '#3498db',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          marginRight: '5px'
                        }}
                      >
                        {assessment.status === 'draft' ? 'Start' : 'Continue'}
                      </Link>
                    ) : (
                      <Link
                        to={`/assessments/${assessment.id}/results`}
                        style={{
                          padding: '5px 10px',
                          backgroundColor: '#27ae60',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          marginRight: '5px'
                        }}
                      >
                        View Results
                      </Link>
                    )}
                    <Link
                      to={`/assessments/${assessment.id}`}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginRight: '5px'
                      }}
                    >
                      Details
                    </Link>
                    {/* Migrate to CSF 2.0 button - Only for NIST CSF 1.1 */}
                    {assessment.assessment_type_name === 'NIST Cybersecurity Framework' && (
                      <button
                        onClick={() => handleMigrateToCSF20(assessment)}
                        style={{
                          padding: '5px 10px',
                          backgroundColor: '#3498db',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          marginRight: '5px'
                        }}
                        title="Migrate this assessment to NIST CSF 2.0"
                      >
                        ➡️ 2.0
                      </button>
                    )}
                    <button
                      onClick={() => handleOpenDuplicateDialog(assessment)}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#9b59b6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        marginRight: '5px'
                      }}
                      title="Duplicate this assessment"
                    >
                      📋
                    </button>
                    <button
                      onClick={() => handleDelete(assessment)}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                      title="Delete this assessment (Admin only)"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Assessment Modal */}
      {showModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>Create New Assessment</h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#999'
                }}
              >
                ×
              </button>
            </div>

            {error && (
              <div style={{
                padding: '10px',
                marginBottom: '20px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '4px',
                color: '#c00'
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>Client *</label>
                <select
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleInputChange}
                  required
                  style={inputStyle}
                >
                  <option value="">Select a client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Assessment Type *</label>
                <select
                  name="assessment_type_id"
                  value={formData.assessment_type_id}
                  onChange={handleInputChange}
                  required
                  style={inputStyle}
                >
                  <option value="">Select an assessment type...</option>
                  {assessmentTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Assessment Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Q1 2026 NIST Assessment"
                  style={inputStyle}
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Optional description or notes..."
                  style={inputStyle}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#27ae60',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.7 : 1
                  }}
                >
                  {submitting ? 'Creating...' : 'Create Assessment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Duplicate Assessment Dialog */}
      {assessmentToDuplicate && (
        <DuplicateAssessmentDialog
          open={duplicateDialogOpen}
          onClose={() => {
            setDuplicateDialogOpen(false);
            setAssessmentToDuplicate(null);
          }}
          assessmentId={assessmentToDuplicate.id}
          assessmentName={assessmentToDuplicate.name}
          onDuplicate={handleDuplicate}
        />
      )}
    </div>
  );
};

const tableHeaderStyle = {
  textAlign: 'left',
  padding: '15px',
  fontWeight: 'bold',
  borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
  padding: '15px'
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000
};

const modalContentStyle = {
  backgroundColor: 'white',
  padding: '30px',
  borderRadius: '8px',
  width: '90%',
  maxWidth: '600px',
  maxHeight: '90vh',
  overflowY: 'auto'
};

const formGroupStyle = {
  marginBottom: '20px'
};

const labelStyle = {
  display: 'block',
  marginBottom: '5px',
  fontWeight: 'bold',
  fontSize: '14px'
};

const inputStyle = {
  width: '100%',
  padding: '10px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '14px',
  boxSizing: 'border-box'
};

export default Assessments;
